import {getFormResultConfig} from "./formResultHelpers.js";

const copy = value => JSON.parse(JSON.stringify(value || {}));
const hasAnswer = value => value !== undefined && value !== null && value !== "";

export function normalizeReporterAssessmentConfig(config = {}) {
	const normalized = copy(config);
	const assessment = normalized.reporterAssessment;
	if (!assessment) return normalized;
	const forms = assessment.forms || {};
	assessment.scale = (Array.isArray(assessment.scale) ? assessment.scale :
		(assessment.anchors || []).map((title, index) => ({title, value: index + (assessment.ratingMin ?? 0)})))
		.map(option => ({value: option.value, title: option.title ?? option.label}));
	assessment.forms = Object.fromEntries(Object.entries(forms).map(([roleId, form]) => {
		const domains = Array.isArray(form.domains) ? form.domains.map(domain => ({id: domain.id, title: domain.title ?? domain.label})) :
			(assessment.domainLabels?.[roleId] || Object.keys(assessment.domainItems?.[roleId] || {})).map(title => ({id: title, title}));
		const assignments = Object.fromEntries(Object.entries(assessment.domainItems?.[roleId] || {}).flatMap(([domainId, numbers]) =>
			(numbers || []).map(number => [String(number), domainId])));
		const items = (form.items || []).map((item, index) => typeof item === "string"
			? {id: String(index + 1), title: item, domainId: assignments[String(index + 1)] || null}
			: {id: item.id == null ? "" : String(item.id), title: item.title ?? item.text, domainId: item.domainId || null});
		return [roleId, {...form, domains, items}];
	}));
	return normalized;
}

export function getReporterAssessmentRoles(config = {}) {
	const forms = config.reporterAssessment?.forms || {};
	const ids = Object.keys(forms);
	const selection = config.reporterSelection;
	return ids.includes(selection) ? [selection] : selection === "both" || !selection ? ids : [];
}

export function getReporterAssessmentConfigErrors(config = {}) {
	if (!config.reporterAssessment) return [];
	const assessment = normalizeReporterAssessmentConfig(config).reporterAssessment;
	const errors = [];
	const scale = assessment.scale || [];
	const uniqueIds = rows => rows.every(row => !!row.id) && new Set(rows.map(row => row.id)).size === rows.length;
	if (!scale.length || new Set(scale.map(option => option.value)).size !== scale.length ||
		scale.some(option => !String(option.title || "").trim() || !Number.isFinite(option.value))) errors.push("Invalid questionnaire scale");
	if (!Object.keys(assessment.forms || {}).length) errors.push("At least one reporter role is required");
	for (const form of Object.values(assessment.forms || {})) {
		const domains = form.domains || [];
		const items = form.items || [];
		const domainIds = new Set(domains.map(domain => domain.id));
		if (!String(form.label || "").trim()) errors.push("Reporter roles need labels");
		if (!uniqueIds(domains) || domains.some(domain => !String(domain.title || "").trim())) errors.push("Domains need unique IDs and labels");
		if (!items.length || !uniqueIds(items) || items.some(item => !String(item.title || "").trim() ||
			(item.domainId && !domainIds.has(item.domainId)))) errors.push("Questions need unique IDs, text, and valid domains");
	}
	return errors;
}

function getOption(scale, answer) {
	return scale.find(option => typeof answer === "number" && option.value === answer);
}

export function getReporterAssessmentAnswers(config = {}, reporter = "", answers = {}) {
	const form = normalizeReporterAssessmentConfig(config).reporterAssessment?.forms?.[reporter];
	if (!form) return {};
	return Object.fromEntries(form.items.flatMap(item => {
		const answer = answers[item.id];
		return hasAnswer(answer) ? [[item.id, answer]] : [];
	}));
}

export function getReporterAssessmentScore(config = {}, reporter = "", answers = {}) {
	const assessment = normalizeReporterAssessmentConfig(config).reporterAssessment || {};
	const form = assessment.forms?.[reporter] || {};
	const items = form.items || [];
	const scale = assessment.scale || [];
	const normalizedAnswers = getReporterAssessmentAnswers(config, reporter, answers);
	const missingItems = [];
	const invalidItems = [];
	let total = 0;
	for (const item of items) {
		const answer = normalizedAnswers[item.id];
		const option = hasAnswer(answer) ? getOption(scale, answer) : null;
		if (!hasAnswer(answer)) missingItems.push(item.id);
		else if (!option || !Number.isFinite(option.value)) {
			missingItems.push(item.id);
			invalidItems.push(item.id);
		} else total += option.value;
	}
	const maximum = scale.length ? Math.max(...scale.map(option => option.value)) : 0;
	const minimum = scale.length ? Math.min(...scale.map(option => option.value)) : 0;
	const domainScores = Object.fromEntries((form.domains || []).map(domain => {
		const members = items.filter(item => item.domainId === domain.id);
		const domainMissingItems = members.filter(item => missingItems.includes(item.id)).map(item => item.id);
		const sum = domainMissingItems.length ? null : members.reduce((value, item) => value + getOption(scale, normalizedAnswers[item.id]).value, 0);
		return [domain.id, {sum, min: members.length * minimum, max: members.length * maximum, itemCount: members.length, missingItems: domainMissingItems}];
	}));
	return {missingItems, invalidItems, total: items.length && !missingItems.length ? total : null,
		totalMin: items.length * minimum, totalMax: items.length * maximum, domainScores};
}

export function getReporterAssessmentView(config = {}, payload = {}, reporterId = "") {
	const normalized = normalizeReporterAssessmentConfig(config);
	const assessment = normalized.reporterAssessment || {};
	const forms = assessment.forms || {};
	const selected = getReporterAssessmentRoles(normalized);
	const reporters = selected.includes(reporterId) ? [reporterId] : selected;
	const anchors = (assessment.scale || []).map(option => ({id: String(option.value), value: option.value, title: option.title}));
	const reports = Object.fromEntries(Object.entries(payload.reports || {}).map(([id, report]) => [id,
		{...report, answers: getReporterAssessmentAnswers(config, id, report?.answers)}]));
	const scores = Object.fromEntries(Object.keys(forms).map(id => [id, getReporterAssessmentScore(normalized, id, reports[id]?.answers)]));
	const ratingLabels = Object.fromEntries(Object.entries(forms).map(([id, form]) => [id, Object.fromEntries(form.items.map(item => {
		const option = getOption(assessment.scale || [], reports[id]?.answers?.[item.id]);
		return [item.id, option ? `${option.value} - ${option.title}` : "Unanswered"];
	}))]));
	const totals = reporters.map(id => scores[id].total);
	const difference = totals.length === 2 && totals.every(total => total !== null) ? Math.abs(totals[0] - totals[1]) : null;
	const domainLabels = Object.fromEntries(Object.entries(forms).map(([id, form]) => [id, form.domains]));
	const domainItems = Object.fromEntries(Object.entries(forms).map(([id, form]) => [id, Object.fromEntries(form.domains.map(domain =>
		[domain.id, form.items.filter(item => item.domainId === domain.id).map(item => item.id)]))]));
	return {forms, reporters, reports, anchors, scores, ratingLabels, difference, domainLabels, domainItems};
}

function hasComparableReporterTotal(currentConfig, previousConfig, reporter) {
	const current = normalizeReporterAssessmentConfig(currentConfig).reporterAssessment || {};
	const previous = normalizeReporterAssessmentConfig(previousConfig).reporterAssessment || {};
	const items = current.forms?.[reporter]?.items || [];
	const priorItems = previous.forms?.[reporter]?.items || [];
	const questions = rows => rows.map(item => [item.id, item.title]).sort(([left], [right]) => left.localeCompare(right));
	return items.length > 0 && JSON.stringify(questions(items)) === JSON.stringify(questions(priorItems)) &&
		JSON.stringify((current.scale || []).map(option => option.value).sort((a, b) => a - b)) ===
		JSON.stringify((previous.scale || []).map(option => option.value).sort((a, b) => a - b));
}

function getSavedAnswerItem(item, form, scale, answers) {
	const value = answers[item.id];
	const option = scale.find(row => row.value === value);
	const domain = form.domains.find(row => row.id === item.domainId);
	return {id: item.id, title: item.title, domainId: item.domainId, domainTitle: domain?.title || "",
		answerTitle: option?.title || String(value), value};
}

export function getReporterAssessmentResultView(config, result = {}, priorResults = []) {
	const savedConfig = getFormResultConfig(undefined, result);
	config = config?.reporterAssessment ? config : savedConfig;
	const current = result.enginePayload?.reports || result.summary?.reports || {};
	const date = Object.values(current)[0]?.date;
	const reports = {...current};
	const reportSnapshots = Object.fromEntries(Object.keys(current).map(id => [id, savedConfig?.reporterAssessment ? savedConfig : config]));
	for (const prior of priorResults) {
		for (const [id, report] of Object.entries(prior.enginePayload?.reports || {})) {
			if (!reports[id] && date && report.date === date) {
				reports[id] = report;
				reportSnapshots[id] = getFormResultConfig(undefined, prior)?.reporterAssessment ? getFormResultConfig(undefined, prior) : config;
			}
		}
	}
	const currentAssessment = normalizeReporterAssessmentConfig(config).reporterAssessment || {};
	const currentForms = currentAssessment.forms || {};
	const archivedItems = {};
	const archivedDomains = {};
	const archivedRoles = {};
	const retainedOptions = {};
	const previousTitles = {};
	const activeReports = Object.fromEntries(Object.entries(reports).map(([id, report]) => {
		const snapshot = reportSnapshots[id];
		const saved = normalizeReporterAssessmentConfig(snapshot).reporterAssessment || {};
		const savedForm = saved.forms?.[id];
		const currentForm = currentForms[id];
		const answers = getReporterAssessmentAnswers(snapshot, id, report.answers);
		if (!currentForm) {
			const form = savedForm || {label: id, domains: [], items: Object.keys(report.answers || {}).map(questionId => ({id: questionId, title: questionId, domainId: null}))};
			const scale = saved.scale || currentAssessment.scale || [];
			archivedRoles[id] = {id, label: form.label || id, form, report: {...report, answers: Object.keys(answers).length ? answers : {...report.answers}},
				anchors: scale.map(option => ({id: String(option.value), value: option.value, title: option.title})),
				score: savedForm ? getReporterAssessmentScore(snapshot, id, report.answers) : null};
			return [id, {...report, answers: {}}];
		}
		const activeAnswers = {};
		archivedItems[id] = [];
		archivedDomains[id] = (savedForm?.domains || []).filter(domain => !currentForm.domains.some(row => row.id === domain.id))
			.map(domain => ({id: domain.id, title: domain.title, items: savedForm.items
				.filter(item => item.domainId === domain.id && hasAnswer(answers[item.id]))
				.map(item => getSavedAnswerItem(item, savedForm, saved.scale || [], answers))}));
		retainedOptions[id] = {};
		previousTitles[id] = {};
		for (const item of savedForm?.items || []) {
			if (!hasAnswer(answers[item.id])) continue;
			const currentItem = currentForm?.items.find(row => row.id === item.id);
			if (currentItem) {
				activeAnswers[item.id] = answers[item.id];
				if (item.title !== currentItem.title) previousTitles[id][item.id] = item.title;
				if (!getOption(currentAssessment.scale || [], answers[item.id])) {
					const savedOption = getOption(saved.scale || [], answers[item.id]);
					if (savedOption) retainedOptions[id][item.id] = {value: savedOption.value, title: savedOption.title};
				}
				continue;
			}
			archivedItems[id].push(getSavedAnswerItem(item, savedForm, saved.scale || [], answers));
		}
		return [id, {...report, answers: activeAnswers}];
	}));
	const view = getReporterAssessmentView(config, {reports: activeReports});
	const priorRows = priorResults.flatMap((prior, index) => Object.entries(prior.enginePayload?.reports || {})
		.filter(([id, report]) => view.forms[id] && report.date !== reports[id]?.date)
		.map(([id, report]) => {
			const priorConfig = getFormResultConfig(undefined, prior)?.reporterAssessment ? getFormResultConfig(undefined, prior) : config;
			const score = getReporterAssessmentScore(priorConfig, id, report.answers);
			const currentTotal = view.scores[id].total;
			return {key: `${index}-${id}`, reporter: id, label: view.forms[id].label || id, date: report.date || "Date missing", total: score.total,
				totalMax: score.totalMax, domainScores: score.domainScores,
				delta: score.total === null || currentTotal === null || !hasComparableReporterTotal(config, priorConfig, id) ? null : currentTotal - score.total};
		}));
	const previousByReporter = Object.fromEntries(view.reporters.map(id => [id, priorRows.filter(row => row.reporter === id && row.date !== "Date missing" && (!reports[id]?.date || row.date < reports[id].date)).sort((a, b) => b.date.localeCompare(a.date))[0] || null]));
	return {...view, archivedItems, archivedDomains, archivedRoles, retainedOptions, previousTitles, priorRows, previousByReporter};
}

export function addReporterAssessmentRole(config = {}, roleId = "", questionId = "") {
	const next = normalizeReporterAssessmentConfig(config);
	const forms = next.reporterAssessment?.forms;
	if (!forms || !roleId || !questionId || forms[roleId]) return next;
	forms[roleId] = {label: "New role", instruction: "", question: "", domains: [],
		items: [{id: questionId, title: "New question", domainId: null}]};
	return next;
}

export function duplicateReporterAssessmentRole(config = {}, sourceRoleId = "", newRoleId = "", createId = () => "") {
	const next = normalizeReporterAssessmentConfig(config);
	const forms = next.reporterAssessment?.forms;
	if (!forms?.[sourceRoleId] || !newRoleId || forms[newRoleId]) return next;
	const source = forms[sourceRoleId];
	const existingIds = new Set(Object.values(forms).flatMap(form => [
		...(form.domains || []).map(domain => domain.id), ...(form.items || []).map(item => item.id),
	]));
	const domainIds = Object.fromEntries(source.domains.map(domain => [domain.id, createId("domain")]));
	const domains = source.domains.map(domain => ({...domain, id: domainIds[domain.id]}));
	const items = source.items.map(item => ({...item, id: createId("question"),
		domainId: item.domainId ? domainIds[item.domainId] || null : null}));
	const generatedIds = [...domains.map(domain => domain.id), ...items.map(item => item.id)];
	if (generatedIds.some(id => !id || existingIds.has(id)) || new Set(generatedIds).size !== generatedIds.length) return next;
	forms[newRoleId] = {...source, label: `${source.label} copy`, domains, items};
	return next;
}

export function removeReporterAssessmentRole(config = {}, roleId = "") {
	const next = normalizeReporterAssessmentConfig(config);
	const forms = next.reporterAssessment?.forms;
	if (!forms?.[roleId] || Object.keys(forms).length <= 1) return next;
	delete forms[roleId];
	if (next.reporterSelection === roleId) next.reporterSelection = "both";
	return next;
}

export function addReporterAssessmentDomain(config = {}, roleId = "", domainId = "") {
	const next = normalizeReporterAssessmentConfig(config);
	const domains = next.reporterAssessment?.forms?.[roleId]?.domains;
	if (domains && domainId && !domains.some(domain => domain.id === domainId)) domains.push({id: domainId, title: "New domain"});
	return next;
}

export function removeReporterAssessmentDomain(config = {}, roleId = "", domainId = "") {
	const next = normalizeReporterAssessmentConfig(config);
	const form = next.reporterAssessment?.forms?.[roleId];
	if (!form) return next;
	form.domains = form.domains.filter(domain => domain.id !== domainId);
	form.items.forEach(item => { if (item.domainId === domainId) item.domainId = null; });
	return next;
}

export function addReporterAssessmentQuestion(config = {}, roleId = "", questionId = "") {
	const next = normalizeReporterAssessmentConfig(config);
	const items = next.reporterAssessment?.forms?.[roleId]?.items;
	if (items && questionId && !items.some(item => item.id === questionId))
		items.push({id: questionId, title: "New question", domainId: null});
	return next;
}

export function removeReporterAssessmentQuestion(config = {}, roleId = "", questionId = "") {
	const next = normalizeReporterAssessmentConfig(config);
	const form = next.reporterAssessment?.forms?.[roleId];
	if (form && form.items.length > 1) form.items = form.items.filter(item => item.id !== questionId);
	return next;
}

export function moveReporterAssessmentQuestion(config = {}, roleId = "", index = 0, offset = 0) {
	const next = normalizeReporterAssessmentConfig(config);
	const items = next.reporterAssessment?.forms?.[roleId]?.items;
	const destination = index + offset;
	if (items && index >= 0 && index < items.length && destination >= 0 && destination < items.length)
		items.splice(destination, 0, items.splice(index, 1)[0]);
	return next;
}

export function addReporterAssessmentScaleOption(config = {}) {
	const next = normalizeReporterAssessmentConfig(config);
	const scale = next.reporterAssessment?.scale;
	if (!scale) return next;
	const value = scale.length ? Math.max(...scale.map(option => option.value)) + 1 : 0;
	scale.push({value, title: `Level ${value}`});
	return next;
}

export function removeReporterAssessmentScaleOption(config = {}, index = -1) {
	const next = normalizeReporterAssessmentConfig(config);
	const scale = next.reporterAssessment?.scale;
	if (scale && scale.length > 1 && index >= 0 && index < scale.length) scale.splice(index, 1);
	return next;
}
